import React, { useEffect, useMemo, useRef } from 'react';
import {
  HtmlEditor,
  Image,
  Video,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  FileManager,
  Count,
  Table,
  Resize,
  ToolbarType,
} from '@syncfusion/ej2-react-richtexteditor';
import { FormControl, FormHelperText, InputLabel } from '@mui/material';
import { useCommonStyles } from 'styles/styles';
/**
 * Default Settings fro File Manager
 */
const defaultFileManagerSettings = {
  enable: true,
  path: '/Pictures/Food',
  ajaxSettings: {
    url: 'https://ej2-aspcore-service.azurewebsites.net/api/FileManager/FileOperations',
    getImageUrl: 'https://ej2-aspcore-service.azurewebsites.net/api/FileManager/GetImage',
    uploadUrl: 'https://ej2-aspcore-service.azurewebsites.net/api/FileManager/Upload',
    downloadUrl: 'https://ej2-aspcore-service.azurewebsites.net/api/FileManager/Download',
  },
};
const SyncFusionEditor = (props) => {
  const {
    error,
    fileManagerCustomSettings,
    isResizableEditor,
    label,
    name,
    editorId,
    editorContent,
    editorPlaceholder,
    injectProps,
    editorValue,
    editorValueTemplate,
    onEditorChange,
    bindDropListener,
    isAutoSaveOnIdle,
    saveOnMillisecondsInterval,
    handleEditorCreationCallback,
    ...otherProps
  } = props;
  /**
   * Uses common styles
   */
  const commonClasses = useCommonStyles();
  /**
   * File manager settings
   */
  const fileManagerSettings = defaultFileManagerSettings || fileManagerCustomSettings;

  const toolbarSettings = {
    items: [
      'Undo',
      'Redo',
      '|',
      'Bold',
      'Italic',
      'Underline',
      'StrikeThrough',
      'FontName',
      'FontSize',
      'FontColor',
      'BackgroundColor',
      'LowerCase',
      'UpperCase',
      '|',
      'Formats',
      'Alignments',
      'OrderedList',
      'UnorderedList',
      'Outdent',
      'Indent',
      '|',
      'CreateLink',
      'Image',
      '|',
      'ClearFormat',
      'Print',
      'SourceCode',
      'FullScreen',
      //
      '|',
      'FileManager',
      'Video',
    ],
    type: ToolbarType.Expand,
    // pasteCleanupSettings: {
    //     deniedTags: ['iframe', 'embed'],
    //     keepFormat: true,
    //     plainText: true,
    //     html: true,
    //     prompt: true
    // }
  };
  /**
   * Editor content data
   */
  const editorContentData = useMemo(() => {
    let tempEditorContentData = <></>;
    if (editorContent) {
      tempEditorContentData = editorContent;
    }

    return tempEditorContentData;
  }, [editorContent]);
  /**
   * Editor reference
   */
  const localEditorRef = useRef();
  /**
   * Editor Created Event bindings
   * @param {*} args
   */
  const handleOnEditorCreated = (args) => {
    let editor = localEditorRef?.current;
    if (typeof bindDropListener === 'function') {
      if (editor?.addEventListener) {
        editor.addEventListener('drop', bindDropListener);
      }
    }
    // editor creation callback
    handleEditorCreationCallback && handleEditorCreationCallback(args, editor);
  };
  /**
   * Effect on Editor Creation
   */
  useEffect(() => {
    let editorNode = localEditorRef?.current;
    if (editorNode) {
      handleOnEditorCreated(editorNode);
    }
  }, [localEditorRef?.current]);
  return (
    <FormControl variant="outlined" fullWidth>
      {label && (
        <InputLabel
          sx={(theme) => ({
            backgroundColor: '#fafafa',
            borderRadius: '5px',
            color: theme.palette.primary.main,
            fontWeight: theme.typography.fontWeightMedium,
            padding: '1px',
          })}
          shrink
          htmlFor={'editor_field' || editorId}
          error={!!error}
        >
          {label}
        </InputLabel>
      )}
      <RichTextEditorComponent
        id={'editor_field' || editorId}
        fileManagerSettings={fileManagerSettings}
        toolbarSettings={toolbarSettings}
        placeholder={editorPlaceholder || null}
        showCharCount={true}
        value={editorValue || null}
        valueTemplate={editorValueTemplate || null}
        height={450}
        ref={localEditorRef}
        created={handleOnEditorCreated}
        change={onEditorChange}
        autoSaveOnIdle={isAutoSaveOnIdle}
        saveInterval={1000}
        className={commonClasses?.classes?.richTextEditorDefaultClass}
        htmlAttributes={{ name: name || editorId }}
        {...otherProps}
      >
        {editorContentData}
        <Inject
          services={[
            Toolbar,
            Image,
            Link,
            HtmlEditor,
            QuickToolbar,
            FileManager,
            Count,
            Table,
            Resize,
            Video,
          ]}
          {...(injectProps || {})}
        />
      </RichTextEditorComponent>
      <FormHelperText
        id={'error-helperText-' + editorId}
        sx={(theme) => ({
          color: theme.palette.error.main,
        })}
        error={error}
      >
        {error || ' '}
      </FormHelperText>
    </FormControl>
  );
};

SyncFusionEditor.defaultProps = {
  isAutoSaveOnIdle: true,
  saveOnMillisecondsInterval: 1000,
};

export default SyncFusionEditor;
